import React from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import MovieFilterOutlinedIcon from '@mui/icons-material/MovieFilterOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AnimationOutlinedIcon from '@mui/icons-material/AnimationOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import BrushOutlinedIcon from '@mui/icons-material/BrushOutlined';
import type { SvgIconComponent } from '@mui/icons-material';

import type { StudioDefinition, VideoOperationType } from '../../../domain';
import { useVideoCatalog } from '../../../queries';
import { hasEligibleModel } from './videoModelCapability';

interface OpMeta {
  label: string;
  description: string;
  icon: SvgIconComponent;
}

export const VIDEO_OPERATION_CATALOG: Record<VideoOperationType, OpMeta> = {
  TextToVideo: { label: 'Text', description: 'Generate a clip from a prompt', icon: MovieFilterOutlinedIcon },
  ImageToVideo: { label: 'Animate', description: 'Animate a still image', icon: ImageOutlinedIcon },
  Interpolate: { label: 'Interpolate', description: 'Tween between two frames', icon: AnimationOutlinedIcon },
  Extend: { label: 'Extend', description: 'Lengthen an existing clip', icon: OpenInFullOutlinedIcon },
  Restyle: { label: 'Restyle', description: 'Re-style an existing clip', icon: BrushOutlinedIcon },
};

export const VIDEO_OPERATION_ORDER: VideoOperationType[] = [
  'TextToVideo',
  'ImageToVideo',
  'Interpolate',
  'Extend',
  'Restyle',
];

/** Studio-curated op list, filtered to known video kinds; full set as fallback. */
export function resolveStudioVideoOperations(studio: StudioDefinition): VideoOperationType[] {
  const valid = (studio.featuredOperations ?? []).filter(k =>
    Object.prototype.hasOwnProperty.call(VIDEO_OPERATION_CATALOG, k),
  ) as VideoOperationType[];
  return valid.length > 0 ? valid : [...VIDEO_OPERATION_ORDER];
}

export function pickDefaultOperation(studio: StudioDefinition): VideoOperationType {
  return resolveStudioVideoOperations(studio)[0] ?? 'TextToVideo';
}

interface Props {
  studio: StudioDefinition;
  activeKind: VideoOperationType;
  onSelect: (kind: VideoOperationType) => void;
}

/** Operation dock for Animation Lab — mirrors Image/Writer Lab's dock. */
export const OperationDock: React.FC<Props> = ({ studio, activeKind, onSelect }) => {
  const { data: catalog, isLoading } = useVideoCatalog();
  // Don't offer an operation no available model can serve. Extend and Restyle each need
  // a specific fal endpoint, and offering them regardless spends credits on a clip that
  // ignores the source. Nothing is hidden while the catalog is still loading, so the
  // dock doesn't flicker on first paint.
  const operations = React.useMemo(() => {
    const curated = resolveStudioVideoOperations(studio);
    if (isLoading || !catalog) return curated;
    return curated.filter(kind => hasEligibleModel(catalog, kind));
  }, [studio, catalog, isLoading]);

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        overflowX: 'auto',
      }}
      aria-label="Video operation dock"
    >
      <ToggleButtonGroup
        value={activeKind}
        exclusive
        onChange={(_e, value: VideoOperationType | null) => value && onSelect(value)}
        size="small"
        sx={{ flexWrap: 'nowrap' }}
      >
        {operations.map(kind => {
          const meta = VIDEO_OPERATION_CATALOG[kind];
          const Icon = meta.icon;
          return (
            <Tooltip key={kind} title={meta.description} placement="top">
              <ToggleButton value={kind} aria-label={meta.label} sx={{ textTransform: 'none' }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon fontSize="small" />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {meta.label}
                  </Typography>
                </Stack>
              </ToggleButton>
            </Tooltip>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
};
